import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const switchRootVariants = cva("", {
  variants: {
    size: {
      sm: "h-5 w-11",
      lg: "h-6 w-14",
    },
  },
  defaultVariants: { size: "sm" },
});

const switchThumbVariants = cva("", {
  variants: {
    size: {
      sm: "h-4 w-6 data-[state=checked]:translate-x-4 group-not-data-disabled:group-hover:w-6.5 group-not-data-disabled:group-active:w-6.5",
      lg: "h-5 w-7 data-[state=checked]:translate-x-6 group-not-data-disabled:group-hover:w-7.5 group-not-data-disabled:group-active:w-7.5",
    },
  },
  defaultVariants: { size: "sm" },
});

type SwitchProps = React.ComponentProps<typeof SwitchPrimitive.Root> &
  VariantProps<typeof switchRootVariants>;

/**
 * Toggle switch with sm and lg size variants.
 *
 * @param size - `"sm"` (default, 44×20) or `"lg"` (56×24)
 * @param className - additional classes merged last
 *
 * @example
 * <Switch size="lg" checked={enabled} onCheckedChange={setEnabled} />
 */
const Switch = ({ className, size, ...props }: SwitchProps) => {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        /* sorted */
        "group box-content relative shrink-0 cursor-pointer",
        "rounded-full",
        "border border-border-dark-min bg-bg-container",
        "transition-[background-color,border-color,opacity] duration-200",
        "data-[state=checked]:border-transparent data-[state=checked]:bg-bg-primary-max",
        "data-[state=unchecked]:hover:border-border-dark-low",
        "data-[state=checked]:hover:bg-bg-primary-max-hover",
        "data-disabled:cursor-not-allowed data-disabled:opacity-[0.48]",
        "data-[state=checked]:data-disabled:opacity-[0.24]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bg-primary-max",
        switchRootVariants({ size }),
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          /* sorted */
          "pointer-events-none absolute left-0.5 top-0.5",
          "rounded-full bg-white shadow-xs",
          "translate-x-0 will-change-transform",
          "group-data-[state=checked]:group-not-data-disabled:group-hover:-ml-0.5",
          "group-data-[state=checked]:group-not-data-disabled:group-active:-ml-0.5",
          "[transition:translate_380ms_cubic-bezier(0.32,0.72,0.24,1.04),width_180ms_ease,margin-left_180ms_ease]",
          switchThumbVariants({ size }),
        )}
      />
    </SwitchPrimitive.Root>
  );
};

export { Switch };
